import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OnboardingLayout } from '../components/OnboardingLayout';
import { WelcomeStep } from './WelcomeStep';
import { OnboardingSessionProvider, useOnboardingSession } from '@/contexts/OnboardingContext';
import { ROUTES } from '@/config/routes';

/**
 * Onboarding Router Logic
 *
 * Handles automatic redirection based on session state (in priority order):
 * 1. completed (completed_at != null): redirect to /dashboard
 * 2. ready_to_finalize (completion_ready_at != null, completed_at == null): redirect to /onboarding/success
 * 3. in_progress with saved steps: redirect to next incomplete step
 * 4. not_started or step 1: stay on welcome screen
 * - Handles loading states
 * - Uses ref guard to prevent infinite navigation loops
 */
function OnboardingRouter() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, isLoadingSession, getNextStep } = useOnboardingSession();

  // Ref to track if navigation has been triggered to prevent infinite loops
  const hasNavigatedRef = useRef(false);

  // Reset navigation flag when path changes (user navigated manually)
  useEffect(() => {
    hasNavigatedRef.current = false;
  }, [location.pathname]);

  useEffect(() => {
    // Wait for session to load
    if (isLoadingSession) {
      return;
    }

    // Prevent re-navigation if we've already navigated
    if (hasNavigatedRef.current) {
      return;
    }

    // Only redirect if user is on the root /onboarding path
    const isOnRootPath = location.pathname === ROUTES.ONBOARDING;

    if (isOnRootPath && session) {
      // PRIORITY 1: If onboarding is fully completed, redirect to dashboard
      if (session.completed_at) {
        hasNavigatedRef.current = true;
        navigate(ROUTES.DASHBOARD, { replace: true });
        return;
      }

      // PRIORITY 2: Check if user is in ready_to_finalize state (Step 4 completed, waiting for tree creation)
      if (session.completion_ready_at && !session.completed_at) {
        hasNavigatedRef.current = true;
        navigate(ROUTES.ONBOARDING_SUCCESS, { replace: true });
        return;
      }

      // PRIORITY 3: If session exists and user has progress beyond step 1, redirect to next incomplete step
      const nextStep = getNextStep();
      if (nextStep > 1) {
        hasNavigatedRef.current = true;
        const targetRoute = `${ROUTES.ONBOARDING}/step-${nextStep}`;
        navigate(targetRoute, { replace: true });
        return;
      }
    }
    // If no session or on step 1, stay on welcome screen (default)
    // No action needed, WelcomeStep is already rendered
  }, [session, isLoadingSession, getNextStep, location.pathname, navigate]);

  const { t } = useTranslation('onboarding');

  // Show loading spinner while fetching session
  if (isLoadingSession) {
    return (
      <OnboardingLayout>
        <main className="flex-1 flex flex-col items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-slate-500">{t('welcome.loading', 'Loading your progress...')}</p>
          </div>
        </main>
      </OnboardingLayout>
    );
  }

  // Render welcome step
  return (
    <OnboardingLayout>
      <WelcomeStep />
    </OnboardingLayout>
  );
}

/**
 * Onboarding Page
 *
 * Main container for the onboarding wizard.
 * Now integrated with persistence layer:
 * - Wraps content with OnboardingSessionProvider
 * - Fetches session on mount
 * - Handles automatic routing to correct step
 * - Provides session state to all child components
 *
 * Phase 1 Features:
 * - Session persistence via React Query hooks
 * - Automatic resume from last step
 * - Loading and error states
 * - Optimistic locking support
 *
 * Future Enhancement (Phase 2+):
 * - Full step 2-4 implementations
 * - Wizard completion flow
 * - Step validation
 * - Autosave functionality
 */
export function OnboardingPage() {
  return (
    <OnboardingSessionProvider>
      <OnboardingRouter />
    </OnboardingSessionProvider>
  );
}
